import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { post } from "@rails/request.js";
import { AnimatePresence, motion } from "framer-motion";
import Loading from "./../shared/Loading";
import {
  SiX, SiLinkedin, SiFacebook, SiReddit,
  SiProducthunt, SiG2, SiTrustpilot, SiYelp, SiGoogle,
  SiMedium, SiYoutube, SiYcombinator, SiDevdotto, SiGithub,
  SiAppstore, SiGoogleplay, SiApplepodcasts,
} from "react-icons/si";
import { FiAlignCenter, FiMic, FiVideo } from "react-icons/fi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import TipTapArea from "./../capform/TipTapArea";
import { Toasts, useToasts } from "./../shared/Toast";
import { ImageUploader } from "./../shared/ImageUploader";
import { VideoUploader } from "./../shared/VideoUploader";
import { AudioUploader } from "./../shared/AudioUploader";
import ProofShow from "./ProofShow";

// Visible row of platform icons under the paste box. ONLY include
// platforms a URL import can realistically pull content from — either
// a dedicated scraper or a public page whose OG tags carry the body.
// Auth-walled platforms (Discord, Slack, etc.) are deliberately
// omitted: showing their icon here implies "this works", and it
// doesn't. The pasted-URL detection still recognizes them and warns,
// so a user who pastes one isn't left guessing why.
const SUPPORTED_PLATFORMS = [
  { key: "Twitter/X",    Icon: SiX,           color: "#000000" },
  { key: "LinkedIn",     Icon: SiLinkedin,    color: "#0A66C2" },
  { key: "Product Hunt", Icon: SiProducthunt, color: "#DA552F" },
  { key: "G2",           Icon: SiG2,          color: "#FF492C" },
  { key: "Trustpilot",   Icon: SiTrustpilot,  color: "#00B67A" },
  { key: "Yelp",         Icon: SiYelp,        color: "#FF1A1A" },
  { key: "Google",       Icon: SiGoogle,      color: "#4285F4" },
  { key: "Reddit",       Icon: SiReddit,      color: "#FF4500" },
  { key: "Facebook",     Icon: SiFacebook,    color: "#1877F2" },
  { key: "Medium",       Icon: SiMedium,      color: "#000000" },
  { key: "YouTube",      Icon: SiYoutube,     color: "#FF0000" },
  { key: "Hacker News",  Icon: SiYcombinator, color: "#FF6600" },
  { key: "DEV.to",       Icon: SiDevdotto,    color: "#000000" },
  { key: "GitHub",       Icon: SiGithub,      color: "#181717" },
  { key: "App Store",      Icon: SiAppstore,      color: "#0D96F6" },
  { key: "Play Store",     Icon: SiGoogleplay,    color: "#01875F" },
  { key: "Apple Podcasts", Icon: SiApplepodcasts, color: "#9933CC" },
];

const KINDS = [
  { key: "text",  label: "Text",  Icon: FiAlignCenter },
  { key: "video", label: "Video", Icon: FiVideo },
  { key: "audio", label: "Audio", Icon: FiMic },
];

// Platforms we have a dedicated, high-fidelity backend importer for —
// not just OpenGraph. Drives the "full auto-import" badge in the UI.
// Keep in sync with the case-list in Importers::UrlSniffer.scraper_for.
const HIGH_FIDELITY = new Set([ "Twitter/X", "Reddit", "YouTube", "Hacker News", "DEV.to", "GitHub" ]);

// Platforms that gate their content behind a login. We can show the
// icon (so people know we recognize the URL) but the importer will
// only ever see the public preview, which is usually the site name
// and not the message body. Surfacing this upfront sets expectations
// before the user fires the import and gets a half-empty proof back.
const AUTH_WALLED = new Set([ "Discord", "Slack", "WhatsApp", "Facebook", "LinkedIn", "Telegram" ]);

// Hostname → SUPPORTED_PLATFORMS key, mirroring the dispatch table in
// app/services/importers/url_sniffer.rb. Kept in JS so we can highlight
// the matching icon the moment the user pastes a URL — no server round
// trip. Keep this in sync with the Ruby PLATFORMS hash if you add
// platforms there.
const HOSTNAME_TO_KEY = {
  "x.com":             "Twitter/X",
  "twitter.com":       "Twitter/X",
  "linkedin.com":      "LinkedIn",
  "producthunt.com":   "Product Hunt",
  "g2.com":            "G2",
  "trustpilot.com":    "Trustpilot",
  "yelp.com":          "Yelp",
  "google.com":        "Google",
  "reddit.com":        "Reddit",
  "facebook.com":      "Facebook",
  "discord.com":       "Discord",
  "slack.com":         "Slack",
  "medium.com":        "Medium",
  "youtube.com":       "YouTube",
  "youtu.be":          "YouTube",
  "news.ycombinator.com": "Hacker News",
  "dev.to":            "DEV.to",
  "github.com":        "GitHub",
  "apps.apple.com":    "App Store",
  "play.google.com":   "Play Store",
  "podcasts.apple.com": "Apple Podcasts",
};

function detectPlatform(url) {
  if (!url) return null;
  let host;
  try {
    host = new URL(url.trim()).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return null;
  }
  const hit = Object.keys(HOSTNAME_TO_KEY).find(
    (suffix) => host === suffix || host.endsWith("." + suffix)
  );
  return hit ? HOSTNAME_TO_KEY[hit] : null;
}

const defaultValues = {
  source_platform: null,
  headline: null,
  body: null,
  name: null,
  title: null,
  avatar: "https://images.shosay.com/placeholder_avatar3.webp",
  company_logo: "https://images.shosay.com/placeholder_avatar2.webp",
  email: null,
  given_at_utc: new Date(),
  source_url: null,
  kind: null,
  company_name: null,
  company_team: null,
};

// Mode the page is in. Drives which sub-panel renders.
//   import:        paste-a-URL hero (single or bulk) + platform strip
//   polling:       spinner while one TransferJob churns
//   bulk_polling:  table of N transfers in flight, one row per pasted URL
//   manual:        the existing big field grid
//   result:        single proof imported / saved — ProofShow card
//   bulk_result:   summary panel listing imported proofs + failures
const MODES = ["import", "polling", "bulk_polling", "manual", "result", "bulk_result"];

// Cap on URLs per bulk paste — keeps the polling fan-out reasonable.
// At 50 URLs and ~5s per import that's still 4+ minutes; users who
// need more should split into batches.
const BULK_MAX = 50;

const ProofNew = ({ kind, project_id }) => {
  const [mode, setMode]           = useState("import");
  const [proof, setProof]         = useState(null);
  const [transfer, setTransfer]   = useState(null);
  // bulkTransfers holds one entry per pasted URL: { url, transfer_id,
  // status: "pending" | "completed" | "failed", proof?, error? }.
  // Both BulkPollingPanel and BulkResultPanel read from this same shape.
  const [bulkTransfers, setBulkTransfers] = useState([]);
  const [importError, setImportError] = useState(null);
  const { toasts, setToasts, addToast } = useToasts();

  const form = useForm({ defaultValues });
  const { formState, register, watch, reset, handleSubmit, setValue, getValues } = form;
  const { errors, isLoading } = formState;

  const handleUpdate = (key, val) => setValue(key, val, { shouldDirty: true, shouldValidate: true });

  // The big form's preview state mirror — same pattern the original used.
  useEffect(() => {
    const subscription = watch(() => {});
    return () => subscription.unsubscribe();
  }, [watch]);

  const resetEverything = () => {
    reset(defaultValues);
    setProof(null);
    setTransfer(null);
    setBulkTransfers([]);
    setImportError(null);
    setMode("import");
  };

  const goManual = (preselectedKind = null) => {
    if (preselectedKind) handleUpdate("kind", preselectedKind);
    setMode("manual");
    // Only push if we aren't already at ?mode=manual — clicking
    // between kind buttons re-enters goManual and we don't want a
    // history entry per click.
    if (new URLSearchParams(window.location.search).get("mode") !== "manual") {
      window.history.pushState({ shosayMode: "manual" }, "", "?mode=manual");
    }
  };

  // Wire the browser back button to the import↔manual toggle. We
  // only sync `manual` to the URL — other transient modes (polling,
  // result, bulk_result) are short-lived and pushing each one would
  // pollute the back stack. Also honors a deep-link: visiting
  // /proofs/new?mode=manual lands directly in the manual form.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("mode") === "manual") {
      setMode("manual");
    }
    const onPopState = () => {
      const m = new URLSearchParams(window.location.search).get("mode");
      setMode(m === "manual" ? "manual" : "import");
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const onSubmitManual = async (data) => {
    const response = await post(`/api/v1/proofs/`, {
      body: {
        project_id,
        proof: {
          kind: data.kind || "text",
          headline: data.headline,
          body: data.body,
          given_at_utc: data.given_at_utc,
          video_url: data.video_url,
          audio_url: data.audio_url,
          approved: true,
          source_tagline: data.source_tagline,
          source_platform: data.source_platform,
          source_url: data.source_url,
        },
        contact: {
          name: data.name,
          email: data.email,
          title: data.title,
          avatar: data.avatar,
          company_name: data.company_name,
          company_team: data.company_team,
          company_logo: data.company_logo,
        },
      },
      contentType: "application/json",
      responseKind: "json",
    });
    if (response.ok) {
      const json = await response.json;
      reset(json.proof);
      setProof(json.proof);
      setMode("result");
      addToast("success", "Saved!");
    } else {
      addToast("error", "Error saving form");
    }
  };

  return (
    <div className="zz-panel p-5 sm:p-8">
      <div className="zz-tape" style={{ background: "rgba(255,122,172,0.85)" }}></div>
      <Toasts toasts={toasts} setToasts={setToasts} />

      {mode === "import" && (
        <ImportHero
          kind={kind}
          project_id={project_id}
          addToast={addToast}
          setMode={setMode}
          setTransfer={setTransfer}
          setBulkTransfers={setBulkTransfers}
          setImportError={setImportError}
          importError={importError}
          goManual={goManual}
        />
      )}

      {mode === "polling" && (
        <PollingPanel
          transfer={transfer}
          setProof={setProof}
          setMode={setMode}
          setImportError={setImportError}
          setTransfer={setTransfer}
          addToast={addToast}
        />
      )}

      {mode === "bulk_polling" && (
        <BulkPollingPanel
          bulkTransfers={bulkTransfers}
          setBulkTransfers={setBulkTransfers}
          setMode={setMode}
          addToast={addToast}
        />
      )}

      {mode === "bulk_result" && (
        <BulkResultPanel
          bulkTransfers={bulkTransfers}
          onAgain={resetEverything}
        />
      )}

      {mode === "manual" && (
        <ManualForm
          kind={kind}
          form={form}
          register={register}
          errors={errors}
          isLoading={isLoading}
          handleUpdate={handleUpdate}
          getValues={getValues}
          handleSubmit={handleSubmit}
          onSubmit={onSubmitManual}
          goBack={() => {
            // Defer to popstate when we're actually in the ?mode=manual
            // URL — that way browser-back and in-app-back follow the
            // same code path. If we got here via deep-link reload or
            // some edge case where the URL doesn't carry ?mode=manual,
            // fall back to a plain state flip so the user isn't stuck.
            if (new URLSearchParams(window.location.search).get("mode") === "manual") {
              window.history.back();
            } else {
              setMode("import");
            }
          }}
        />
      )}

      {mode === "result" && proof?.id && (
        <ResultPanel proof={proof} onAgain={resetEverything} />
      )}
    </div>
  );
};

// ─── Paste-a-URL hero ────────────────────────────────────────────────────

const ImportHero = ({
  kind, project_id, addToast, setMode, setTransfer, setBulkTransfers, setImportError, importError, goManual,
}) => {
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkError, setBulkError] = useState(null);

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({ defaultValues: { url: "" } });

  // Live platform detection. Recomputed every keystroke; cheap.
  const urlValue   = watch("url");
  const matchedKey = detectPlatform(urlValue);

  const onSubmit = async (data) => {
    setImportError(null);
    const response = await post(`/api/v1/proofs/import_from_url`, {
      body: { project_id, url: data.url },
      contentType: "application/json",
      responseKind: "json",
    });
    if (response.ok) {
      const json = await response.json;
      setTransfer(json.transfer);
      setMode("polling");
      reset({ url: "" });
    } else {
      addToast("error", "Couldn't start the import");
    }
  };

  // Split the textarea on newlines, trim, drop blanks, dedupe in-order,
  // and validate the http(s) shape — same regex the single-URL field uses.
  const parseBulkUrls = (text) => {
    const seen = new Set();
    const urls = [];
    for (const raw of text.split(/\r?\n/)) {
      const u = raw.trim();
      if (!u || seen.has(u)) continue;
      if (!/^https?:\/\/.+/i.test(u)) continue;
      seen.add(u);
      urls.push(u);
    }
    return urls;
  };

  const onBulkSubmit = async () => {
    setBulkError(null);
    const urls = parseBulkUrls(bulkText);
    if (urls.length === 0) {
      setBulkError("Paste at least one URL — one per line, http or https.");
      return;
    }
    if (urls.length > BULK_MAX) {
      setBulkError(`That's ${urls.length} URLs — split into batches of ${BULK_MAX} or fewer.`);
      return;
    }

    // Fire all N import_from_url POSTs in parallel — the endpoint just
    // creates a Transfer and enqueues a job, so it's cheap and fast.
    const results = await Promise.all(urls.map(async (url) => {
      const response = await post(`/api/v1/proofs/import_from_url`, {
        body: { project_id, url },
        contentType: "application/json",
        responseKind: "json",
      });
      if (response.ok) {
        const json = await response.json;
        return { url, transfer_id: json.transfer.id, status: "pending" };
      }
      return { url, transfer_id: null, status: "failed", error: "Couldn't start the import" };
    }));

    setBulkTransfers(results);
    setMode("bulk_polling");
    setBulkText("");
  };

  // Live count of valid URLs in the bulk textarea — drives the
  // "import N URLs" button label and the "X / 50 used" hint.
  const bulkUrls = bulkMode ? parseBulkUrls(bulkText) : [];

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="zz-hand text-2xl text-ssblue leading-none">
              {bulkMode ? "paste a list —" : "paste a link —"}
            </p>
            <h2 className="zz-display text-2xl sm:text-3xl font-black leading-tight">
              {bulkMode ? <>grab many <span className="zz-highlight yellow">{kind}s</span>.</>
                        : <>we'll grab your <span className="zz-highlight yellow">{kind}</span>.</>}
            </h2>
          </div>
          <button
            type="button"
            onClick={() => { setBulkMode(!bulkMode); setBulkError(null); }}
            className="zz-link text-sm whitespace-nowrap mt-2"
          >
            {bulkMode ? "← single URL" : "paste a list →"}
          </button>
        </div>
        <p className="text-sm text-slate-600 mt-2 max-w-xl">
          {bulkMode
            ? <>One URL per line — up to {BULK_MAX}. Each runs through the same importer pipeline; supported platforms get full auto-import, anything else falls back to OpenGraph meta tags.</>
            : <>Drop a URL from any of the platforms below — or any other public webpage — and we'll extract what we can. Twitter/X gets the full pull; everything else gets the basics (headline, body, source) and you fill in the rest.</>}
        </p>

        {!bulkMode && (
          <>
            <div className="mt-5 flex items-stretch gap-2 flex-wrap">
              <input
                type="text"
                {...register("url", { required: true, pattern: { value: /^https?:\/\/.+/i, message: "needs http:// or https://" } })}
                autoFocus
                placeholder="https://…"
                spellCheck="false"
                className="zz-input flex-1 min-w-[200px] text-base"
              />
              <button onClick={handleSubmit(onSubmit)} className="zz-btn blue">
                import →
              </button>
            </div>
            {errors.url && (
              <p className="text-xs text-ssred mt-1">{errors.url.message || "paste a URL"}</p>
            )}
          </>
        )}

        {bulkMode && (
          <>
            <div className="mt-5">
              <textarea
                autoFocus
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={"https://twitter.com/foo/status/1\nhttps://news.ycombinator.com/item?id=8863\nhttps://dev.to/jane/great-post\n…"}
                rows={8}
                spellCheck="false"
                className="zz-input w-full text-sm font-mono leading-relaxed"
              />
              <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs text-slate-500">
                  {bulkUrls.length}/{BULK_MAX} valid URLs detected
                </p>
                <button
                  type="button"
                  onClick={onBulkSubmit}
                  disabled={bulkUrls.length === 0}
                  className={`zz-btn blue ${bulkUrls.length === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  import {bulkUrls.length || ""} {bulkUrls.length === 1 ? "URL" : "URLs"} →
                </button>
              </div>
            </div>
            {bulkError && (
              <p className="text-xs text-ssred mt-2">{bulkError}</p>
            )}
          </>
        )}

        {/* Icon strip is single-URL-only. In bulk mode the strip
            doesn't have a single URL to react to, so it's just noise. */}
        {!bulkMode && (
        <>
        <div className="mt-3 flex flex-wrap gap-2">
          {SUPPORTED_PLATFORMS.map((p) => {
            const Icon = p.Icon;
            const isMatch = matchedKey === p.key;
            // Three states: nothing matched (all normal), this one matched
            // (color ring + slight pop), another one matched (dimmed).
            const base = "inline-flex items-center gap-1.5 px-2.5 py-1 border-[2px] rounded-full text-xs font-bold transition-all duration-150";
            const styled = matchedKey == null
              ? `${base} border-black bg-white`
              : isMatch
                ? `${base} bg-white border-black scale-110 shadow-[3px_3px_0_rgba(0,0,0,0.85)]`
                : `${base} border-black/30 bg-white/50 opacity-40`;
            return (
              <span
                key={p.key}
                title={HIGH_FIDELITY.has(p.key)
                  ? `${p.key} — full auto-import`
                  : AUTH_WALLED.has(p.key)
                    ? `${p.key} — auth-walled, the importer only sees the logged-out preview`
                    : `${p.key} — we'll extract what we can from the public page`}
                className={styled}
                style={isMatch ? { boxShadow: `0 0 0 2px ${p.color}, 3px 3px 0 rgba(0,0,0,0.85)` } : undefined}
              >
                <Icon style={{ color: p.color }} className="text-sm" /> {p.key}
              </span>
            );
          })}
        </div>
        <p className="text-xs text-slate-500 mt-2">
          {matchedKey && HIGH_FIDELITY.has(matchedKey) ? (
            <><span className="font-bold text-slate-700">recognized: {matchedKey}</span> — full auto-import.</>
          ) : matchedKey && AUTH_WALLED.has(matchedKey) ? (
            <><span className="font-bold text-ssred">{matchedKey} is auth-walled</span> — we can only see what a logged-out visitor sees, which usually isn't the message body. Paste the content manually for the best result.</>
          ) : matchedKey ? (
            <><span className="font-bold text-slate-700">recognized: {matchedKey}</span> — we'll extract what we can from the public page.</>
          ) : (
            <>+ any other public URL — we'll pull headline, description, date, and image from its meta tags.</>
          )}
        </p>
        </>
        )}

        {importError && (
          <div className="mt-4 zz-panel border-ssred bg-ssred/5 p-3 text-sm">
            <p className="font-bold text-ssred">Couldn't import that one.</p>
            <p className="text-slate-700 mt-1">{importError}</p>
            <p className="text-xs text-slate-500 mt-2">
              Try a different URL, or{" "}
              <button onClick={() => goManual()} className="zz-link inline">enter it manually →</button>
            </p>
          </div>
        )}

        <div className="zz-divider"></div>

        <p className="zz-hand text-lg text-slate-500">no link to paste? —</p>
        <div className="flex flex-wrap gap-2 mt-2">
          {KINDS.map((k) => {
            const Icon = k.Icon;
            return (
              <button
                key={k.key}
                type="button"
                onClick={() => goManual(k.key)}
                className="zz-btn sm"
              >
                <span className="flex items-center gap-2">
                  <Icon /> enter {k.label.toLowerCase()} manually
                </span>
              </button>
            );
          })}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// ─── Polling spinner ─────────────────────────────────────────────────────

const PollingPanel = ({ transfer, setProof, setMode, setImportError, setTransfer, addToast }) => {
  // Long-poll the transfer endpoint. Backs off slightly after the first hit.
  useEffect(() => {
    if (!transfer?.id) return;
    let cancelled = false;
    let attempts = 0;

    const tick = async () => {
      attempts += 1;
      try {
        const res = await fetch(`/api/v1/transfers/${transfer.id}`, {
          headers: { Accept: "application/json" },
        });
        const json = await res.json();
        if (cancelled) return;
        if (json.proof) {
          setProof(json.proof);
          setMode("result");
          setTransfer(null);
          addToast("success", "Imported!");
          return;
        }
        if (json.transfer?.status === "failed") {
          setImportError(json.transfer.error_message || "Couldn't read that page.");
          setMode("import");
          setTransfer(null);
          return;
        }
        // still pending — back off after the first few tries
        const delay = attempts < 4 ? 2000 : 4000;
        setTimeout(tick, delay);
      } catch (e) {
        if (cancelled) return;
        setImportError("Network hiccup while checking the import.");
        setMode("import");
        setTransfer(null);
      }
    };

    setTimeout(tick, 1500);
    return () => { cancelled = true; };
  }, [transfer?.id]);

  return (
    <div className="text-center py-12">
      <Loading />
      <p className="zz-hand text-xl text-slate-500 mt-4">pulling it in —</p>
      <p className="text-sm text-slate-600 mt-1">This usually takes 5–20 seconds.</p>
    </div>
  );
};

// ─── Manual entry form ───────────────────────────────────────────────────

const ManualForm = ({
  kind, register, errors, isLoading, handleUpdate, getValues, handleSubmit, onSubmit, goBack,
}) => {
  const body         = getValues().body;
  const avatar       = getValues().avatar;
  const company_logo = getValues().company_logo;
  const given_at_utc = getValues().given_at_utc;
  const video_url    = getValues().video_url;
  const audio_url    = getValues().audio_url;
  const currentKind  = getValues().kind || "text";

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <p className="zz-hand text-2xl text-ssblue leading-none">manual —</p>
            <p className="zz-display text-2xl sm:text-3xl font-black leading-tight capitalize">
              {currentKind} {kind}
            </p>
          </div>
          <button type="button" onClick={goBack} className="zz-link muted text-sm">
            ← back to import
          </button>
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          {KINDS.map((k) => (
            <button
              key={k.key}
              type="button"
              onClick={() => handleUpdate("kind", k.key)}
              className={`zz-btn sm ${currentKind === k.key ? "blue" : ""}`}
            >
              <span className="flex items-center gap-2">
                <k.Icon /> {k.label}
              </span>
            </button>
          ))}
        </div>

        <p className="zz-section">the {kind} itself</p>
        <div className="space-y-4">
          <div>
            <label className="zz-label">
              Headline <span className="ml-1 text-ssred">*</span>
              {errors.headline && <span className="ml-2 text-ssred normal-case tracking-normal text-xs font-normal">required</span>}
            </label>
            <input type="text" {...register("headline", { required: true })} autoFocus placeholder="Give it a short, punchy headline" spellCheck="false" className="zz-input" />
          </div>

          {currentKind === "video" && (
            <div>
              <label className="zz-label">Video file</label>
              <VideoUploader setUrl={(url) => handleUpdate("video_url", url)} initialUrl={video_url} />
            </div>
          )}
          {currentKind === "audio" && (
            <div>
              <label className="zz-label">Audio file</label>
              <AudioUploader setUrl={(url) => handleUpdate("audio_url", url)} initialUrl={audio_url} />
            </div>
          )}
          {currentKind === "text" && (
            <div>
              <label className="zz-label">
                Body <span className="ml-1 text-ssred">*</span>
                {errors.body && <span className="ml-2 text-ssred normal-case tracking-normal text-xs font-normal">required</span>}
              </label>
              <div className="border-[3px] border-black rounded-lg overflow-hidden">
                <TipTapArea html={body} updateHtml={(val) => handleUpdate("body", val)} />
              </div>
            </div>
          )}
        </div>

        <div className="zz-divider"></div>

        <p className="zz-section green">who gave it to you?</p>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-5 mb-4">
          <div className="sm:col-span-2">
            <label className="zz-label">Avatar</label>
            <ImageUploader initialImageUrl={avatar} setUrl={(url) => handleUpdate("avatar", url)} />
          </div>
          <div className="sm:col-span-3 flex flex-col gap-3">
            <div>
              <label className="zz-label">
                Name <span className="ml-1 text-ssred">*</span>
                {errors.name && <span className="ml-2 text-ssred normal-case tracking-normal text-xs font-normal">required</span>}
              </label>
              <input type="text" {...register("name", { required: true })} placeholder="Their name" spellCheck="false" className="zz-input" />
            </div>
            <div>
              <label className="zz-label">
                Email
                {errors.email && <span className="ml-2 text-ssred normal-case tracking-normal text-xs font-normal">{errors.email.message}</span>}
              </label>
              <input type="email" placeholder="hello@example.com" {...register("email", { pattern: { value: /\S+@\S+\.\S+/, message: "Bad email format" } })} spellCheck="false" className="zz-input" />
            </div>
          </div>
        </div>

        <p className="zz-section orange">their company (if any)</p>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-5 mb-4">
          <div className="sm:col-span-2">
            <label className="zz-label">Company logo</label>
            <ImageUploader initialImageUrl={company_logo} setUrl={(url) => handleUpdate("company_logo", url)} />
          </div>
          <div className="sm:col-span-3 flex flex-col gap-3">
            <div>
              <label className="zz-label">Job title</label>
              <input type="text" placeholder="Designer" {...register("title")} spellCheck="false" className="zz-input" />
            </div>
            <div>
              <label className="zz-label">Company</label>
              <input type="text" placeholder="Acme Bricks Inc" {...register("company_name")} spellCheck="false" className="zz-input" />
            </div>
            <div>
              <label className="zz-label">Team</label>
              <input type="text" placeholder="Design" {...register("company_team")} spellCheck="false" className="zz-input" />
            </div>
          </div>
        </div>

        <p className="zz-section">other bits</p>
        <div className="grid gap-4 sm:grid-cols-3 mb-4">
          <div>
            <label className="zz-label">When was it? <span className="ml-1 text-ssred">*</span></label>
            <DatePicker className="zz-input" selected={given_at_utc} withPortal onChange={(date) => handleUpdate("given_at_utc", date)} />
          </div>
          <div>
            <label className="zz-label">Source platform</label>
            <input type="text" {...register("source_platform")} placeholder="Twitter / Email / etc." spellCheck="false" className="zz-input" />
          </div>
          <div>
            <label className="zz-label">URL it came from</label>
            <input type="text" {...register("source_url")} placeholder="https://example.com" spellCheck="false" className="zz-input" />
          </div>
        </div>

        <div aria-hidden="true" className="h-12 sm:h-4"></div>
        <div className="flex items-center justify-center sm:justify-end gap-3 flex-wrap">
          <button
            type="button"
            onClick={handleSubmit(onSubmit)}
            disabled={isLoading}
            className={`zz-btn blue ${isLoading ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            {isLoading ? "saving…" : `save ${kind} ✦`}
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// ─── Bulk polling — many transfers in flight at once ─────────────────────

const BulkPollingPanel = ({ bulkTransfers, setBulkTransfers, setMode, addToast }) => {
  // Independent poller per transfer. Each runs until that transfer's
  // status flips to "completed" or "failed", then it stops. When every
  // transfer has finished, we flip the parent mode to "bulk_result".
  useEffect(() => {
    let cancelled = false;

    const pollOne = async (index) => {
      let attempts = 0;
      // Loop until terminal state or cancelled.
      while (!cancelled) {
        attempts += 1;
        const entry = bulkTransfersRef.current[index];
        if (!entry || entry.status !== "pending" || !entry.transfer_id) return;

        try {
          const res = await fetch(`/api/v1/transfers/${entry.transfer_id}`, {
            headers: { Accept: "application/json" },
          });
          const json = await res.json();
          if (cancelled) return;

          if (json.proof) {
            updateEntry(index, { status: "completed", proof: json.proof });
            return;
          }
          if (json.transfer?.status === "failed") {
            updateEntry(index, { status: "failed", error: json.transfer.error_message || "Import failed" });
            return;
          }
          // Still pending — sleep before the next poll.
        } catch (e) {
          if (cancelled) return;
          updateEntry(index, { status: "failed", error: "Network hiccup" });
          return;
        }
        await new Promise(r => setTimeout(r, attempts < 4 ? 2000 : 4000));
      }
    };

    // Kick off one poller per pending entry. Keep a live ref to the
    // current array so pollers can re-read it after each update.
    bulkTransfersRef.current = bulkTransfers;
    bulkTransfers.forEach((t, i) => { if (t.status === "pending") pollOne(i); });
    return () => { cancelled = true; };
    // We intentionally depend on length only — entries' status changes
    // are surfaced through the ref, not by re-running the effect.
  }, [bulkTransfers.length]);

  // Ref mirror of the state array. The pollers run as long-lived loops;
  // closing over the state at-effect-time would mean each poll reads
  // stale data. The ref is the escape hatch.
  const bulkTransfersRef = useRef(bulkTransfers);
  bulkTransfersRef.current = bulkTransfers;

  const updateEntry = (index, patch) => {
    setBulkTransfers(prev => {
      const next = prev.slice();
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const total     = bulkTransfers.length;
  const done      = bulkTransfers.filter(t => t.status !== "pending").length;
  const succeeded = bulkTransfers.filter(t => t.status === "completed").length;
  const failed    = bulkTransfers.filter(t => t.status === "failed").length;
  const allDone   = done === total;

  // When everything has resolved, hop to the result panel automatically
  // so the user doesn't have to click through.
  useEffect(() => {
    if (allDone) {
      addToast("success", `Imported ${succeeded} of ${total}`);
      setMode("bulk_result");
    }
  }, [allDone]);

  return (
    <div>
      <p className="zz-hand text-2xl text-ssblue leading-none">pulling them in —</p>
      <h2 className="zz-display text-2xl sm:text-3xl font-black leading-tight">
        {done} of {total} done.
      </h2>
      <div className="mt-4 h-2 w-full rounded-full overflow-hidden bg-slate-200 border-2 border-black">
        <div className="h-full bg-ssblue transition-all duration-300"
             style={{ width: `${total ? (done / total) * 100 : 0}%` }} />
      </div>

      <div className="mt-5 space-y-1 text-sm">
        {bulkTransfers.map((t, i) => (
          <BulkRow key={i} entry={t} />
        ))}
      </div>
    </div>
  );
};

const BulkRow = ({ entry }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-200 last:border-0">
    <span className="truncate min-w-0 flex-1 text-slate-700 font-mono text-xs">{entry.url}</span>
    {entry.status === "pending"   && <span className="text-[10px] font-black uppercase bg-slate-100 text-slate-700 px-2 py-0.5 rounded">pending</span>}
    {entry.status === "completed" && <span className="text-[10px] font-black uppercase bg-ssgreen/20 text-ssgreen-dark px-2 py-0.5 rounded">imported</span>}
    {entry.status === "failed"    && <span title={entry.error || ""} className="text-[10px] font-black uppercase bg-ssred/20 text-ssred px-2 py-0.5 rounded">failed</span>}
  </div>
);

// ─── Bulk result summary ─────────────────────────────────────────────────

const BulkResultPanel = ({ bulkTransfers, onAgain }) => {
  const succeeded = bulkTransfers.filter(t => t.status === "completed");
  const failed    = bulkTransfers.filter(t => t.status === "failed");

  return (
    <div>
      <p className="zz-hand text-2xl text-ssgreen leading-none">batch done —</p>
      <h2 className="zz-display text-2xl sm:text-3xl font-black leading-tight">
        {succeeded.length} imported{failed.length > 0 && <>, <span className="text-ssred">{failed.length} failed</span></>}.
      </h2>
      <p className="text-sm text-slate-600 mt-2">
        Imported proofs land as drafts in your proofs list — review &amp; approve from there.
      </p>

      {succeeded.length > 0 && (
        <>
          <p className="zz-section green mt-5">imported</p>
          <div className="space-y-1 text-sm">
            {succeeded.map((t, i) => (
              <div key={i} className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-200 last:border-0">
                <span className="truncate min-w-0 flex-1 font-bold text-slate-900">{t.proof?.headline || t.proof?.body?.slice(0, 80) || "Untitled"}</span>
                <a href={t.url} target="_blank" rel="noopener" className="text-xs text-slate-500 font-mono truncate max-w-[200px]">{t.url}</a>
              </div>
            ))}
          </div>
        </>
      )}

      {failed.length > 0 && (
        <>
          <p className="zz-section orange mt-5">couldn't import</p>
          <div className="space-y-1 text-sm">
            {failed.map((t, i) => (
              <div key={i} className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-200 last:border-0">
                <span className="truncate min-w-0 flex-1 font-mono text-xs text-slate-600">{t.url}</span>
                <span className="text-xs text-ssred">{t.error || "failed"}</span>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="flex justify-end mt-6">
        <button onClick={onAgain} className="zz-btn sm blue">import another batch</button>
      </div>
    </div>
  );
};

// ─── Imported / saved result ─────────────────────────────────────────────

const ResultPanel = ({ proof, onAgain }) => (
  <AnimatePresence>
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}>
      <p className="zz-hand text-2xl text-ssgreen leading-none">{proof.approved ? "saved ✓" : "imported as draft ✓"}</p>
      <p className="zz-display text-2xl sm:text-3xl font-black mb-4">
        {proof.approved ? "Done." : "Review & approve →"}
      </p>
      <ProofShow proof={proof} />
      <div className="flex justify-end mt-4 gap-2">
        <button onClick={onAgain} className="zz-btn sm blue">import another</button>
      </div>
    </motion.div>
  </AnimatePresence>
);

export default ProofNew;
